import { useState, useCallback } from "react";
import { Platform } from "react-native";
import { router } from "expo-router";
import useAccessCreateCheckout from "@hooks/trpc/useAccessCreateCheckout";
import * as rudderstack from "@utils/rudderstack";
import { captureException } from "@utils/captureException";
import { Access } from "@hooks/trpc/useAccesses";
import { usePayment } from "@utils/stripe/usePayment";
import sdk from "@farcaster/miniapp-sdk";
import { useFarcasterContext } from "@hooks/useFarcasterContext";
import Alert from "@components/Alert";

interface UseCheckoutParams {
  /** An event off the engine (old rail): music unlocks in its own sheet
   *  (LIS-1038). */
  access: Extract<Access, { type: "event" }>;
  numberOfTickets: number;
  pricePerTicket: number;
  unlockCode?: string;
  useGems?: boolean;
  confirmedAmountDueMinorUnits?: number;
}

interface UseCheckoutReturn {
  handlePay: (email?: string) => Promise<void>;
  isProcessingPayment: boolean;
}

export const useCheckout = ({
  access,
  numberOfTickets,
  pricePerTicket,
  unlockCode,
  useGems,
  confirmedAmountDueMinorUnits,
}: UseCheckoutParams): UseCheckoutReturn => {
  const accessId = access.id;
  const [isProcessingEventPayment, setIsProcessingEventPayment] =
    useState(false);
  const createPaymentSheetMutation = useAccessCreateCheckout();
  const { isInMiniApp } = useFarcasterContext();

  const bookingFee = access.bookingFee ?? 0;

  // Initialize payment hook for mobile payment sheet
  const { openPaymentSheet } = usePayment(
    accessId,
    numberOfTickets,
    pricePerTicket - bookingFee,
    useGems,
    confirmedAmountDueMinorUnits,
  );

  const handlePaymentSuccess = useCallback(
    (userId?: string, email?: string) => {
      if (userId && email) {
        rudderstack
          .identify({
            userId,
            email,
          })
          .catch((error) => {
            captureException(error, {
              channel: "breadcrumb",
              area: "analytics",
              operation: "identify-after-payment-success",
            });
          });
      }

      // Note: RudderStack tracking is now handled by the backend
      console.log("::handlePaymentSuccess - native: going back two screens");
      router.setParams({
        paymentResult: "success",
        accessId,
      });
    },
    [accessId],
  );

  const handleEventPay = useCallback(
    async (email?: string) => {
      setIsProcessingEventPayment(true);

      // On web, call checkout endpoint directly and redirect to Stripe
      if (Platform.OS === "web") {
        try {
          console.log("🔄 Calling checkout endpoint...");

          // Get current URL origin
          const currentUrl = new URL(window.location.href);

          // Create success URL pointing to access detail screen
          const successUrl = new URL(`/access/${accessId}`, currentUrl.origin);
          successUrl.searchParams.set("paymentResult", "success");

          // Create cancel URL pointing to the current checkout screen
          const cancelUrl = new URL(window.location.href);
          cancelUrl.searchParams.set("paymentResult", "cancel");

          const response = await createPaymentSheetMutation.mutateAsync({
            accessId: accessId,
            numberOfTickets: numberOfTickets,
            purchasePlatform: "app-web",
            successUrl: successUrl.toString(),
            cancelUrl: cancelUrl.toString(),
            customPrice: pricePerTicket - bookingFee,
            email,
            unlockCode,
            useGems,
            confirmedAmountDueMinorUnits,
          });


          const userId =
            response && "userId" in response ? response.userId : undefined;

          if (
            response &&
            "status" in response &&
            response.status === "quote-changed"
          ) {
            Alert.alert("Price changed", "Please review checkout again.");
            setIsProcessingEventPayment(false);
            return;
          }

          if (response && "kind" in response && response.kind === "engine") {
            if (isInMiniApp) {
              await sdk.actions.openUrl({ url: response.url });
            } else {
              window.location.assign(response.url);
            }
            return;
          }
          // Check if payment was free (no checkoutUrl or paymentIntent)
          if (
            !response ||
            (!("checkoutUrl" in response) && !("paymentIntent" in response))
          ) {
            // Payment was free and tickets were issued
            console.log(
              "Payment was free, tickets issued - redirecting to access detail",
            );
            handlePaymentSuccess(userId ?? undefined, email);
            return;
          }

          // For web, we get a checkoutUrl instead of paymentIntent
          if ("checkoutUrl" in response && response.checkoutUrl) {
            console.log("🔄 Redirecting to Stripe Checkout...");
            // NOTE: Don't call handlePaymentSuccess here - payment hasn't happened yet!
            // Success will be handled when user returns from Stripe via the successUrl callback

            // In Farcaster mini app, use SDK to open external URL
            if (isInMiniApp) {
              console.log("🔄 Using Farcaster SDK to open Stripe URL...");
              await sdk.actions.openUrl({ url: response.checkoutUrl });
            } else {
              // Regular web - direct redirect
              window.location.href = response.checkoutUrl;
            }
            return;
          }

          throw new Error("Invalid response from checkout endpoint");
        } catch (error) {
          console.error("❌ Payment initialization error:", error);
          setIsProcessingEventPayment(false);
        }
        return;
      } else if (unlockCode) {
        // Mobile free unlock flow — bypass payment sheet
        try {
          await createPaymentSheetMutation.mutateAsync({
            accessId,
            numberOfTickets,
            purchasePlatform: Platform.OS === "ios" ? "ios" : "android",
            customPrice: 0,
            unlockCode,
            useGems: false,
            confirmedAmountDueMinorUnits: 0,
          });
          handlePaymentSuccess();
        } catch (error) {
          console.error("Unlock error:", error);
        } finally {
          setIsProcessingEventPayment(false);
        }
      } else {
        // Mobile payment sheet flow
        const success = await openPaymentSheet({});

        if (success) {
          handlePaymentSuccess();
        }
        setIsProcessingEventPayment(false);
      }
    },
    [
      accessId,
      numberOfTickets,
      pricePerTicket,
      bookingFee,
      createPaymentSheetMutation,
      handlePaymentSuccess,
      openPaymentSheet,
      isInMiniApp,
      unlockCode,
      useGems,
      confirmedAmountDueMinorUnits,
    ],
  );

  return {
    handlePay: handleEventPay,
    isProcessingPayment: isProcessingEventPayment,
  };
};
